import { useState, useEffect } from 'react';
import { notificationService } from '../services/notificationService';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

/**
 * Custom hook for real-time notification counter
 * Tracks unread notifications for duel invites, verification, and results
 */
export const useNotificationCounter = () => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) {
      setUnreadCount(0);
      setIsLoading(false);
      return;
    }

    // Fetch initial unread count
    const fetchUnreadCount = async () => {
      try {
        const count = await notificationService?.getUnreadCount(user?.id);
        setUnreadCount(count || 0);
      } catch (error) {
        console.error('Error fetching unread count:', error);
        setUnreadCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUnreadCount();

    // Subscribe to real-time notification changes
    const channel = supabase
      ?.channel(`notifications:${user?.id}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user?.id}`
        },
        (payload) => {
          // Refetch count on any notification change
          fetchUnreadCount();
        }
      )
      ?.subscribe();

    // Cleanup subscription
    return () => {
      channel?.unsubscribe();
    };
  }, [user?.id]);

  return { unreadCount, isLoading, refetch: async () => {
    if (user?.id) {
      try {
        const count = await notificationService?.getUnreadCount(user?.id);
        setUnreadCount(count || 0);
      } catch (error) {
        console.error('Error refetching unread count:', error);
      }
    }
  }};
};

export default useNotificationCounter;